import { useEffect, useState } from "react";
import { api, type ApiResult } from "../api.js";
import { useLocale } from "../ui.js";
import { safeErrorCode } from "@smart-shikka/react";

interface ExtensionReleaseData {
  version: string;
  packageUrl: string;
  sha256: string;
  releaseNotesUrl: string;
  installMode: "private_unpacked" | "store";
}

export function BrowserExtensionSetupPanel({ token }: { token?: string | undefined }): React.JSX.Element {
  const { tr, locale } = useLocale();
  const [release, setRelease] = useState<ExtensionReleaseData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    void api.publicBrowserExtensionRelease().then((res: ApiResult<ExtensionReleaseData>) => {
      if (!live) return;
      setLoading(false);
      if (res.ok && res.data) {
        setRelease(res.data);
        setError(null);
      } else {
        setRelease(null);
        setError(safeErrorCode(res.error ?? res.denialCode ?? "unavailable"));
      }
    });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="panel-stack">
      <div className="panel-card">
        <div className="panel-card-header">
          <div>
            <div className="eyebrow">{tr("browserExtension")}</div>
            <h2>{tr("browserExtensionSetup")}</h2>
          </div>
          <span className="ss-pill gray" role="status">{tr("browserExtensionStatus")}</span>
        </div>
        <p>{tr("browserExtensionStatusHelp")}</p>
        {token ? null : <p className="muted">{locale === "hi" ? "Owner session न होने पर pairing status backend से fetch नहीं हो सकता।" : "Pairing status cannot be fetched without an owner session."}</p>}
      </div>

      <div className="panel-card">
        <div className="eyebrow">{tr("browserExtensionDownload")}</div>
        {loading ? (
          <p className="muted">{tr("loading")}</p>
        ) : release ? (
          <>
            <p>{tr("browserExtensionDownloadHelp")}</p>
            <ul className="prop-list">
              <li><strong>{tr("browserExtensionVersion")}:</strong> {release.version}</li>
              <li><strong>{tr("browserExtensionSha256")}:</strong> <code className="break-all">{release.sha256}</code></li>
              <li><strong>{tr("browserExtensionInstallMode")}:</strong> {release.installMode === "store" ? tr("browserExtensionStore") : tr("browserExtensionPrivateUnpacked")}</li>
            </ul>
            <a
              href={release.packageUrl}
              className="ss-button primary"
              target="_blank"
              rel="noreferrer"
              download
            >
              {tr("browserExtensionDownload")}
            </a>
            {release.releaseNotesUrl ? (
              <p className="muted">
                <a href={release.releaseNotesUrl} target="_blank" rel="noreferrer">{locale === "hi" ? "Release notes" : "Release notes"}</a>
              </p>
            ) : null}
          </>
        ) : (
          <p className="warning">{tr("browserExtensionDownloadUnavailable")} {error ? `(${error})` : ""}</p>
        )}
      </div>

      <div className="panel-card">
        <div className="eyebrow">{tr("browserExtensionSetup")}</div>
        <ol>
          <li>{tr("browserExtensionStepOne")}</li>
          <li>{tr("browserExtensionStepTwo")}</li>
          <li>{tr("browserExtensionStepThree")}</li>
        </ol>
      </div>
    </div>
  );
}
